import React, { useState, useEffect, useContext, useCallback } from "react";
import { NotificationContext } from "./NotificationContext";
import axios from "axios";
import { useLocation } from "react-router-dom";
import isAuth from "../is-Auth/IsAuthContext";

const Backend_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationState = ({ children }) => {
  const [allNotifications, setAllNotifications] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const { token, user } = useContext(isAuth);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${Backend_URL}/api/notifications/all`, {
        headers: { token },
      });
      setAllNotifications(data.notifications);
      setUnreadCount(data.notifications.reduce((count, n) => count + !n.read, 0));
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("An error occurred, please reload your page");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchMatches = useCallback(async () => {
    if (!token || !user || location.pathname !== "/notification/matches") return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${Backend_URL}/api/notifications/matches`, {
        headers: { token },
      });
      setAllMatches(
        data.matches.map((match) => ({
          ...match,
          isFiltered:
            (match.scoreRequestBy === user._id && match.status === "score-requested") ||
            (match.creator === user._id && match.status === "in-progress"),
        }))
      );
    } catch (err) {
      console.error("Error fetching matches:", err);
      setError("An error occurred, please reload your page");
    } finally {
      setLoading(false);
    }
  }, [token, user, location.pathname]);

  useEffect(() => {
    fetchNotifications();
    fetchMatches();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchMatches]);

  const markAllNotificationsAsRead = async () => {
    try {
      await axios.put(`${Backend_URL}/api/notifications/mark-read`,{} ,{ headers: { token } });
      setAllNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking notifications as read:", err);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        allNotifications,
        allMatches,
        loading,
        error,
        user,
        unreadCount,
        markAllNotificationsAsRead
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationState;